import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import httpClient from "@/config/axios";

export type NoticeDetailItem = {
  id: number;
  title: string;
  content: string;
  author_name: string;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
};

/** 더미 데이터 - 개발/테스트용 */
const mockNoticeDetails: Record<number, NoticeDetailItem> = {
  1: {
    id: 1,
    title: "[공지] 시스템 점검 안내",
    content: `안녕하세요. 메디통 온:마음 AI 근무표 운영팀입니다.

2026년 3월 20일(수) 오전 2시부터 오전 4시까지 시스템 점검이 진행됩니다.

점검 시간 동안 서비스 이용이 제한될 수 있으니 양해 부탁드립니다.

문의사항이 있으시면 고객지원센터로 연락 주시기 바랍니다.

감사합니다.`,
    author_name: "관리자",
    is_pinned: true,
    created_at: "2026-03-15T10:00:00",
    updated_at: "2026-03-15T10:00:00",
  },
  2: {
    id: 2,
    title: "신규 기능 업데이트 안내",
    content: `새로운 기능이 추가되었습니다.

1. 근무표 자동 생성 기능 개선
2. 알림 설정 기능 추가
3. 통계 대시보드 업데이트

자세한 내용은 사용 가이드를 참고해 주세요.`,
    author_name: "운영팀",
    is_pinned: false,
    created_at: "2026-03-14T14:30:00",
    updated_at: "2026-03-14T14:30:00",
  },
  3: {
    id: 3,
    title: "근무표 작성 가이드",
    content: `근무표 작성 방법을 안내드립니다.

1. 좌측 메뉴에서 '근무표 작성'을 선택하세요.
2. 날짜와 근무 시간을 선택하세요.
3. 저장 버튼을 클릭하여 완료하세요.

추가 문의사항이 있으시면 언제든지 문의해 주세요.`,
    author_name: "관리자",
    is_pinned: false,
    created_at: "2026-03-13T09:15:00",
    updated_at: "2026-03-13T09:15:00",
  },
  4: {
    id: 4,
    title: "이벤트 안내 - 신규 가입자 혜택",
    content: `신규 가입자를 위한 특별 이벤트가 진행됩니다!

이벤트 기간: 2026년 3월 1일 ~ 3월 31일

혜택:
- 첫 달 무료 이용
- 프리미엄 기능 30일 무료 체험
- 1:1 온라인 상담 서비스

많은 참여 부탁드립니다.`,
    author_name: "마케팅팀",
    is_pinned: false,
    created_at: "2026-03-12T16:45:00",
    updated_at: "2026-03-12T16:45:00",
  },
  5: {
    id: 5,
    title: "문의사항 접수 방법 안내",
    content: `문의사항 접수 방법을 안내드립니다.

1. 고객지원 > 문의하기 메뉴를 선택하세요.
2. 문의 유형과 내용을 작성하세요.
3. 제출 버튼을 클릭하여 접수하세요.

평균 답변 시간: 1-2일 소요됩니다.

감사합니다.`,
    author_name: "고객지원팀",
    is_pinned: false,
    created_at: "2026-03-11T11:20:00",
    updated_at: "2026-03-11T11:20:00",
  },
};

/**
 * 공지사항 상세를 가져오는 커스텀 훅
 * @param noticeId 공지사항 ID
 * @param enabled 쿼리 활성화 여부 (기본 true)
 * @returns useQuery 결과
 */
export function useNoticeDetail(
  noticeId: number | null,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: QUERY_KEYS.notice.detail(noticeId ?? 0),
    queryFn: async () => {
      if (!noticeId) return null;

      // 더미 데이터 사용 (개발/테스트용)
      // return mockNoticeDetails[noticeId] || null;

      // 실제 API 호출 (주석 처리)
      return await httpClient.get<NoticeDetailItem>(
        `/contact/notice/detail/${noticeId}`
      );
    },
    enabled: enabled && noticeId !== null,
  });
}
